"use client";

import * as Sentry from "@sentry/nextjs";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Routes } from "src/lib/routes";
import { usePaymentComplete } from "src/lib/use-payment-complete";

export type ExpressCheckoutPhase = "idle" | "processing" | "success";

const SUCCESS_DELAY_MS = 2000;
/** Per-attempt budget for /api/process-payment before we treat it as hung. */
const PROCESS_PAYMENT_TIMEOUT_MS = 10_000;
/** Backoff before each retry (initial attempt has no delay). */
const RETRY_BACKOFFS_MS = [500, 1500];

/** Query params Stripe + our redirect handler append after a wallet payment.
 *  Stripped on success so the cleaned URL doesn't retrigger the flow on a
 *  refresh and doesn't carry sensitive intent IDs around. */
const EXPRESS_QUERY_PARAMS = [
  "customer_id",
  "express",
  "setup_intent",
  "setup_intent_client_secret",
  "redirect_status",
  "payment_intent",
  "payment_intent_client_secret",
] as const;

interface UseExpressCheckoutRedirectOptions {
  /** Invoked once /api/process-payment finishes, after the success display. */
  onPaymentSuccess: () => void;
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms
    );
    promise.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

/**
 * Handles the Stripe Express Checkout (Apple Pay / Google Pay) redirect.
 *
 * Stripe redirects back to /home with `?customer_id=...&express=true&setup_intent=...`
 * after a successful wallet payment. The setup_intent.succeeded webhook is the
 * sole creator of the subscription; this hook only reconciles our DB by setting
 * `paymentId` on the account so that subscription checks succeed.
 *
 * Resilience:
 *  - Wraps each /api/process-payment attempt in a 10s timeout so a hung fetch
 *    can't strand the user on the processing screen.
 *  - Retries up to 2 additional times with backoff. The most common transient
 *    cause is the auth-cookie write race: the home wizard's ensureAuthCookie
 *    effect and this hook's effect both fire after the same render, and on a
 *    cold paint the fetch can race ahead of the cookie landing.
 *  - On final failure, allows a back-nav to retry by clearing the started
 *    flag before redirecting to /error.
 */
export function useExpressCheckoutRedirect({
  onPaymentSuccess,
}: UseExpressCheckoutRedirectOptions): { phase: ExpressCheckoutPhase } {
  const router = useRouter();
  const searchParams = useSearchParams();
  const processPayment = usePaymentComplete();

  const expressCustomerId = searchParams.get("customer_id");
  // setup_intent is required for the redirect to be considered valid (Stripe
  // appends it on success), but we don't read its value — only its presence.
  const isExpressRedirect =
    searchParams.get("express") === "true" &&
    !!expressCustomerId &&
    !!searchParams.get("setup_intent");

  const [phase, setPhase] = useState<ExpressCheckoutPhase>(
    isExpressRedirect ? "processing" : "idle"
  );

  // Latest callbacks via refs so the effect doesn't re-fire (and prematurely
  // cancel the success timer) when the parent re-renders with a new identity.
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  onPaymentSuccessRef.current = onPaymentSuccess;
  const processPaymentRef = useRef(processPayment);
  processPaymentRef.current = processPayment;
  const expressCustomerIdRef = useRef(expressCustomerId);
  expressCustomerIdRef.current = expressCustomerId;
  const routerRef = useRef(router);
  routerRef.current = router;

  // One-shot guard: at most one reconciliation attempt per mount.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isExpressRedirect || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    (async () => {
      const delays = [0, ...RETRY_BACKOFFS_MS];
      let lastErr: unknown;
      for (let i = 0; i < delays.length; i++) {
        if (cancelled) return;
        if (delays[i] > 0) {
          await new Promise((r) => setTimeout(r, delays[i]));
          if (cancelled) return;
        }
        try {
          await withTimeout(
            processPaymentRef.current({
              customerId: expressCustomerIdRef.current!,
              subscriptionId: "",
            }),
            PROCESS_PAYMENT_TIMEOUT_MS,
            "/api/process-payment"
          );
          lastErr = undefined;
          break;
        } catch (err) {
          lastErr = err;
          Sentry.addBreadcrumb({
            message: `express_checkout reconcile attempt ${i + 1} failed`,
            level: "warning",
            data: {
              error: err instanceof Error ? err.message : String(err),
            },
          });
        }
      }

      if (cancelled) return;

      if (lastErr) {
        Sentry.captureException(lastErr, {
          tags: { source: "express_checkout_redirect" },
        });
        const message =
          lastErr instanceof Error
            ? lastErr.message
            : "Payment processing failed";
        // Un-arm so a back-nav (which keeps the URL params) can retry.
        startedRef.current = false;
        setPhase("idle");
        routerRef.current.push(
          `${Routes.ERROR}?message=${encodeURIComponent(message)}`
        );
        return;
      }

      setPhase("success");
      timer = setTimeout(() => {
        if (cancelled) return;
        onPaymentSuccessRef.current();
        setPhase("idle");
        // Strip the express/setup query params but stay on the current
        // page. Apple Pay always redirects back to the URL Stripe was
        // given as `return_url`, which is now caller-configurable (e.g.
        // /intake/{id}); we no longer want to yank the user back to /home.
        const url = new URL(window.location.href);
        EXPRESS_QUERY_PARAMS.forEach((p) => url.searchParams.delete(p));
        routerRef.current.replace(`${url.pathname}${url.search}`);
      }, SUCCESS_DELAY_MS);
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Deps intentionally exclude `phase` and the values held in refs above:
    // - `phase` is an OUTPUT of this effect; including it would let the
    //   cleanup run mid-flight and clear the success timer.
    // - Other inputs are read from refs so a parent re-render with a fresh
    //   callback identity doesn't tear down in-flight work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpressRedirect]);

  return { phase };
}
